import { buildImgproxyUrl } from './buildImgproxyUrl';

/**
 * The pixel widths that a drawn image may ask imgproxy for. A size between two steps takes the
 * next step up, so a page asks for few different URLs and Cloudflare keeps few copies.
 */
export const DRAWN_IMAGE_PIXEL_STEPS: readonly number[] = [64, 128, 192, 256, 384, 512, 768, 1024, 1536, 1920];

/** The largest step: imgproxy caps every side at 1920 px. */
export const DRAWN_IMAGE_MAX_PIXELS = 1920;

/** A screen with more than 3 device pixels for each CSS pixel gets 3: the eye sees no difference. */
export const DRAWN_IMAGE_MAX_DPR = 3;

/** The pixel width to ask for: the CSS size times the device pixel ratio, up to the next step. */
export function drawnImagePixels(cssPx: number, devicePixelRatio: number = 1): number {
  const dpr = Number.isFinite(devicePixelRatio) && devicePixelRatio > 0 ? devicePixelRatio : 1;
  const wanted = Math.ceil(Math.max(1, cssPx) * Math.min(dpr, DRAWN_IMAGE_MAX_DPR));
  return DRAWN_IMAGE_PIXEL_STEPS.find((step) => step >= wanted) ?? DRAWN_IMAGE_MAX_PIXELS;
}

export interface DrawnImageInput {
  /** The base URL of imgproxy, with the trailing slash (the app config `imageProcessingURL`). */
  imgproxyBase: string;
  /** An absolute URL that imgproxy may fetch (on `IMGPROXY_ALLOWED_SOURCES`). */
  sourceUrl: string;
  /** The size of the image on the page, in CSS pixels. */
  cssPx: number;
  devicePixelRatio?: number;
  /** Crop to a square (a cover). Default true. False keeps the shape and fits the width. */
  square?: boolean;
}

/**
 * The imgproxy URL of an image as the page draws it: sized for the place, always JPEG.
 *
 * Always JPEG: Cloudflare does not honour `Vary: Accept`, so a negotiated URL can give a cached
 * WebP to a client that cannot read it. Our imgproxy URLs are not signed (`insecure/`), so a
 * client may build them.
 */
export function drawnImageUrl(input: DrawnImageInput): string {
  const pixels = drawnImagePixels(input.cssPx, input.devicePixelRatio);
  const square = input.square ?? true;
  return buildImgproxyUrl(input.imgproxyBase, input.sourceUrl, {
    width: pixels,
    height: square ? pixels : undefined,
    quality: 82,
    makeSquareFit: square,
    gravity: square ? 'ce' : undefined,
    forceJPEG: true,
  });
}
